import React from 'react';
import { Box, Typography, Button, Modal, Paper, Fade, Grow, keyframes } from '@mui/material';
import { useAuth0 } from '@auth0/auth0-react';
import { Logout, Warning } from '@mui/icons-material';

interface SessionExpiredMessageProps {
  onLogout?: () => void;
}

// Define keyframes for animations
const pulse = keyframes`
  0%, 100% { 
    transform: scale(1); 
    opacity: 0.1; 
  }
  50% { 
    transform: scale(1.1); 
    opacity: 0.2; 
  }
`;

const rotate = keyframes`
  from { 
    transform: rotate(0deg); 
  }
  to { 
    transform: rotate(360deg); 
  }
`;

const SessionExpiredMessage: React.FC<SessionExpiredMessageProps> = ({
  onLogout
}) => {
  const { logout } = useAuth0();

  const handleLogout = () => {
    if (onLogout) {
      onLogout();
    } else {
      // Proper Auth0 logout
      logout({
        logoutParams: {
          returnTo: window.location.origin,
        },
      });
    }
  };

  return (
    <Modal
      open={true}
      disableEscapeKeyDown
      disableAutoFocus
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 9999,
        p: 2
      }}
    >
      <Fade in={true} timeout={300}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '100%',
            maxWidth: 480,
          }}
        >
          <Grow in={true} timeout={500}>
            <Paper
              elevation={0}
              sx={{
                p: 0,
                width: '100%',
                borderRadius: 4,
                overflow: 'hidden',
                background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                boxShadow: [
                  '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                  '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
                  '0 20px 25px -5px rgba(0, 0, 0, 0.1)',
                  '0 0 0 1px rgba(255, 255, 255, 0.05)'
                ],
                backdropFilter: 'blur(20px)',
                position: 'relative',
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  height: '4px',
                  background: 'linear-gradient(90deg, #3b82f6 0%, #06b6d4 50%, #8b5cf6 100%)',
                }
              }}
            >
              {/* Header with gradient background */}
              <Box
                sx={{
                  background: 'linear-gradient(135deg, #eff6ff 0%, #f0f9ff 100%)',
                  p: 4,
                  textAlign: 'center',
                  borderBottom: '1px solid rgba(0, 0, 0, 0.06)',
                  position: 'relative',
                  overflow: 'hidden',
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: -50,
                    right: -50,
                    width: 100,
                    height: 100,
                    borderRadius: '50%',
                    background: 'radial-gradient(circle, rgba(59, 130, 246, 0.1) 0%, transparent 70%)',
                    animation: `${pulse} 2s ease-in-out infinite`
                  }
                }}
              >
                {/* Icon with animated background */}
                <Box
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 80,
                    height: 80,
                    borderRadius: '50%',
                    background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
                    mb: 3,
                    position: 'relative',
                    boxShadow: '0 8px 32px rgba(59, 130, 246, 0.3)',
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      top: -2,
                      left: -2,
                      right: -2,
                      bottom: -2,
                      borderRadius: '50%',
                      background: 'linear-gradient(135deg, #3b82f6, #06b6d4, #8b5cf6)',
                      zIndex: -1,
                      animation: `${rotate} 3s linear infinite`
                    }
                  }}
                >
                  <Warning 
                    sx={{ 
                      fontSize: 40, 
                      color: 'white',
                      filter: 'drop-shadow(0 2px 4px rgba(0, 0, 0, 0.1))'
                    }} 
                  />
                </Box>

                <Typography 
                  variant="h4" 
                  sx={{ 
                    fontWeight: 700, 
                    color: '#1e293b',
                    mb: 1,
                    fontSize: { xs: '1.75rem', sm: '2rem' },
                    letterSpacing: '-0.025em'
                  }}
                >
                  Session Expired
                </Typography>
                
                <Typography 
                  variant="body1" 
                  sx={{ 
                    color: '#475569',
                    lineHeight: 1.6,
                    fontSize: '1.1rem',
                    maxWidth: 320,
                    mx: 'auto'
                  }}
                >
                  Your authentication session has expired. Please log in again to continue.
                </Typography>
              </Box>

              {/* Action section */}
              <Box sx={{ p: 4, textAlign: 'center' }}>
                <Button
                  variant="contained"
                  size="large"
                  onClick={handleLogout}
                  startIcon={<Logout sx={{ fontSize: 20 }} />}
                  sx={{ 
                    px: 4,
                    py: 1.5,
                    fontSize: '1.1rem',
                    fontWeight: 600,
                    borderRadius: 3,
                    background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
                    boxShadow: '0 4px 14px rgba(59, 130, 246, 0.4)',
                    textTransform: 'none',
                    minWidth: 200,
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #1d4ed8 0%, #1e40af 100%)',
                      boxShadow: '0 6px 20px rgba(59, 130, 246, 0.6)',
                      transform: 'translateY(-2px)'
                    },
                    '&:active': {
                      transform: 'translateY(0)'
                    }
                  }}
                >
                  Sign In Again
                </Button>

                <Typography 
                  variant="caption" 
                  sx={{ 
                    display: 'block',
                    mt: 3,
                    color: '#94a3b8',
                    fontSize: '0.875rem'
                  }}
                >
                  This will redirect you to the login page
                </Typography>
              </Box>
            </Paper>
          </Grow>
        </Box>
      </Fade>
    </Modal>
  );
};

export default SessionExpiredMessage;
